import React from "react";

function Bone({ w, h, r = 4, className = "", style = {} }) {
  return (
    <span
      className={`scheduling-skeleton-bone ${className}`.trim()}
      style={{
        width: typeof w === "number" ? `${w}px` : w,
        height: typeof h === "number" ? `${h}px` : h,
        borderRadius: typeof r === "number" ? `${r}px` : r,
        ...style,
      }}
      aria-hidden
    />
  );
}

function Wrap({ theme, className = "", children }) {
  return (
    <div
      className={`scheduling-skeleton ${
        theme === "light"
          ? "scheduling-skeleton--light"
          : "scheduling-skeleton--dark"
      } ${className}`.trim()}
      role="status"
      aria-label="Loading"
    >
      {children}
    </div>
  );
}

/** Classes & Facilities list — mirrors .scheduling-class-card geometry */
export function CardListSkeleton({ theme = "dark", rows = 5 }) {
  return (
    <Wrap theme={theme} className="scheduling-skeleton-cardlist">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="scheduling-skeleton-cardlist-row">
          <Bone
            w="1.25rem"
            h="1.25rem"
            r={3}
            className="scheduling-skeleton-cardlist-check"
          />
          <Bone
            w="9rem"
            h="6rem"
            r="0.5rem"
            className="scheduling-skeleton-cardlist-thumb"
          />
          <div className="scheduling-skeleton-cardlist-body">
            <Bone w="40%" h="1rem" />
            <Bone w="65%" h="0.875rem" />
            <div className="scheduling-skeleton-cardlist-meta">
              <Bone w="100%" h="0.875rem" />
              <Bone w="100%" h="0.875rem" />
              <Bone w="100%" h="0.875rem" />
              <Bone w="80%" h="0.875rem" />
              <Bone w="80%" h="0.875rem" />
              <Bone w="80%" h="0.875rem" />
            </div>
          </div>
          <div className="scheduling-skeleton-cardlist-coaches">
            <Bone w="2rem" h="2rem" r="50%" />
            <Bone w="2rem" h="2rem" r="50%" />
            <Bone w="2rem" h="2rem" r="50%" />
          </div>
          <div className="scheduling-skeleton-cardlist-actions">
            <Bone w="4rem" h="2rem" r="0.5rem" />
            <Bone w="2rem" h="2rem" r="0.5rem" />
          </div>
        </div>
      ))}
    </Wrap>
  );
}

/** PT Sessions list — mirrors .scheduling-pt-session-card geometry */
export function PtSessionListSkeleton({ theme = "dark", rows = 5 }) {
  return (
    <Wrap theme={theme} className="scheduling-skeleton-pt">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="scheduling-skeleton-pt-row">
          <Bone w="1.25rem" h="1.25rem" r={3} />
          <Bone w="1.75rem" h="1.75rem" r={4} />
          <Bone w="3rem" h="3rem" r="50%" />
          <div className="scheduling-skeleton-pt-name">
            <Bone w="10rem" h="0.875rem" />
            <Bone w="7rem" h="0.75rem" />
          </div>
          <div className="scheduling-skeleton-pt-cols">
            <div className="scheduling-skeleton-pt-col">
              <Bone w="4rem" h="0.75rem" />
              <Bone w="6rem" h="0.875rem" />
            </div>
            <div className="scheduling-skeleton-pt-col">
              <Bone w="2.5rem" h="0.75rem" />
              <Bone w="10rem" h="0.875rem" />
            </div>
            <div className="scheduling-skeleton-pt-col">
              <Bone w="2.5rem" h="0.75rem" />
              <Bone w="8rem" h="0.875rem" />
            </div>
          </div>
          <div className="scheduling-skeleton-pt-actions">
            <Bone w="4rem" h="2rem" r="0.5rem" />
            <Bone w="2rem" h="2rem" r="0.5rem" />
          </div>
        </div>
      ))}
    </Wrap>
  );
}

/** Bookings table — mirrors .scheduling-bookings-row 7-col grid */
export function BookingsTableSkeleton({ theme = "dark", rows = 8 }) {
  return (
    <Wrap theme={theme} className="scheduling-skeleton-bookings">
      <div className="scheduling-skeleton-bookings-head">
        <Bone w="4rem" h="0.875rem" />
        <Bone w="6rem" h="0.875rem" />
        <Bone w="5rem" h="0.875rem" />
        <Bone w="3rem" h="0.875rem" />
        <Bone w="5rem" h="0.875rem" />
        <Bone w="3rem" h="0.875rem" />
        <Bone w="3.5rem" h="0.875rem" />
      </div>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="scheduling-skeleton-bookings-row">
          <Bone w="3rem" h="0.875rem" />
          <div className="scheduling-skeleton-bookings-member">
            <Bone w="2rem" h="2rem" r="50%" />
            <Bone w="6rem" h="0.875rem" />
          </div>
          <Bone w="10rem" h="0.875rem" />
          <Bone w="5rem" h="1.25rem" r="0.625rem" />
          <Bone w="9rem" h="0.875rem" />
          <Bone w="4rem" h="0.875rem" />
          <Bone w="6rem" h="0.875rem" />
        </div>
      ))}
    </Wrap>
  );
}

/** History event cards — mirrors .scheduling-history-event-card */
export function HistoryCardSkeleton({ theme = "dark", rows = 5 }) {
  return (
    <Wrap theme={theme} className="scheduling-skeleton-history">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="scheduling-skeleton-history-row">
          <Bone
            w="13rem"
            h="8rem"
            r="0.75rem"
            className="scheduling-skeleton-history-thumb"
          />
          <div className="scheduling-skeleton-history-body">
            <Bone w="14rem" h="1rem" />
            <Bone w="9rem" h="0.875rem" />
            <div className="scheduling-skeleton-history-meta">
              <Bone w="6rem" h="0.875rem" />
              <Bone w="5rem" h="0.875rem" />
              <Bone w="6rem" h="0.875rem" />
            </div>
          </div>
          <Bone
            w="10rem"
            h="2.25rem"
            r="1.125rem"
            className="scheduling-skeleton-history-cta"
          />
        </div>
      ))}
    </Wrap>
  );
}

/** Blackouts list — mirrors .scheduling-blackout-row 6-col grid */
export function BlackoutRowSkeleton({ theme = "dark", rows = 6 }) {
  return (
    <Wrap theme={theme} className="scheduling-skeleton-blackouts">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="scheduling-skeleton-blackouts-row">
          <Bone w="1.25rem" h="1.25rem" r={3} />
          <div className="scheduling-skeleton-blackouts-lead">
            <Bone w="10rem" h="1rem" />
            <Bone w="6rem" h="0.75rem" />
          </div>
          <div className="scheduling-skeleton-blackouts-col">
            <Bone w="2.5rem" h="0.75rem" />
            <Bone w="5rem" h="0.875rem" />
          </div>
          <div className="scheduling-skeleton-blackouts-col">
            <Bone w="4.5rem" h="0.75rem" />
            <Bone w="6.5rem" h="0.875rem" />
          </div>
          <div className="scheduling-skeleton-blackouts-col">
            <Bone w="2.5rem" h="0.75rem" />
            <Bone w="7rem" h="0.875rem" />
          </div>
          <div className="scheduling-skeleton-blackouts-actions">
            <Bone w="3.5rem" h="2rem" r="0.5rem" />
            <Bone w="1.75rem" h="2rem" r="0.5rem" />
          </div>
        </div>
      ))}
    </Wrap>
  );
}

/** Overview weekly grid — mirrors .scheduling-overview-week-* geometry */
export function OverviewCalendarSkeleton({ theme = "dark" }) {
  const blocksPerCol = [4, 5, 4, 5, 4, 3, 5];
  return (
    <Wrap theme={theme} className="scheduling-skeleton-overview">
      <div className="scheduling-skeleton-overview-head">
        {blocksPerCol.map((_, c) => (
          <div key={c} className="scheduling-skeleton-overview-head-cell">
            <Bone w="2rem" h="0.75rem" />
            <Bone w="1.5rem" h="1.125rem" />
          </div>
        ))}
      </div>
      <div className="scheduling-skeleton-overview-body">
        {blocksPerCol.map((count, c) => (
          <div key={c} className="scheduling-skeleton-overview-col">
            {Array.from({ length: count }, (_, b) => (
              <Bone
                key={b}
                w="100%"
                h="5rem"
                r="0.375rem"
                className="scheduling-skeleton-overview-event"
              />
            ))}
          </div>
        ))}
      </div>
    </Wrap>
  );
}

/** Tours list */
export function ToursListSkeleton({ theme = "dark", rows = 3 }) {
  return (
    <Wrap theme={theme} className="scheduling-skeleton-tours">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="scheduling-skeleton-tours-row">
          <Bone w="1.25rem" h="1.25rem" r={4} />
          <div className="scheduling-skeleton-tours-main">
            <div className="scheduling-skeleton-tours-header">
              <Bone w="8rem" h="1rem" />
              <div className="scheduling-skeleton-tours-actions">
                <Bone w="4.5rem" h="2rem" r="0.5rem" />
                <Bone w="5rem" h="2rem" r="0.5rem" />
              </div>
            </div>
            <div className="scheduling-skeleton-tours-body">
              <Bone w="14rem" h="1.25rem" r="0.75rem" />
              <div className="scheduling-skeleton-tours-cols">
                <div className="scheduling-skeleton-tours-col">
                  <Bone w="3rem" h="0.75rem" />
                  <Bone w="7rem" h="0.875rem" />
                </div>
                <div className="scheduling-skeleton-tours-col">
                  <Bone w="3rem" h="0.75rem" />
                  <Bone w="8rem" h="0.875rem" />
                </div>
                <div className="scheduling-skeleton-tours-col">
                  <Bone w="4rem" h="0.75rem" />
                  <Bone w="10rem" h="0.875rem" />
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </Wrap>
  );
}

/** Settings page — mirrors .scheduling-settings-card.scheduling-settings-card-cols */
export function SettingsSkeleton({ theme = "dark" }) {
  return (
    <Wrap theme={theme} className="scheduling-skeleton-settings">
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="scheduling-skeleton-settings-card">
          <div className="scheduling-skeleton-settings-head">
            <Bone w="10rem" h="1rem" />
            <Bone w="14rem" h="0.875rem" />
            <Bone w="11rem" h="0.875rem" />
          </div>
          <div className="scheduling-skeleton-settings-body">
            <Bone w="100%" h="3rem" r="0.5rem" />
            {i > 0 && <Bone w="100%" h="3rem" r="0.5rem" />}
          </div>
        </div>
      ))}
    </Wrap>
  );
}
